import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ChevronRight } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: 'Framing', path: '/services#framing' },
    { name: 'Drywall & Taping', path: '/services#drywall' },
    { name: 'Painting', path: '/services#painting' },
    { name: 'Siding', path: '/services#siding' },
    { name: 'Flooring', path: '/services#flooring' },
    { name: 'Interior Finishing', path: '/services#interior-finishing' },
  ];

  const quickLinks = [
    { name: 'About Us', path: '/services' },
    { name: 'Our Work', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
    { name: 'Get a Quote', path: '/contact' },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_ss-build-preview/artifacts/jzohyi1e_CL%20S%26S%20GROUP%20SERVICES%20CREATION%20COMPANY.png" 
                alt="S&S Group Services Construction" 
                className="h-20 w-auto"
              />
            </div>
            <p className="text-slate-400 mb-6">
              Professional construction services specializing in commercial and residential projects across British Columbia.
            </p>
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 bg-slate-800 text-xs text-slate-300 uppercase tracking-wider">Licensed</span>
              <span className="px-3 py-1 bg-slate-800 text-xs text-slate-300 uppercase tracking-wider">Insured</span>
              <span className="px-3 py-1 bg-slate-800 text-xs text-slate-300 uppercase tracking-wider">Locally Owned & Operated</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link 
                    to={service.path} 
                    className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 group"
                    data-testid={`footer-service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ChevronRight className="w-4 h-4 text-blue-500 group-hover:translate-x-1 transition-transform" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <ChevronRight className="w-4 h-4 text-blue-500 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold uppercase tracking-wider mb-6" style={{ fontFamily: 'Oswald, sans-serif' }}>Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:+12363380659" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5 text-blue-500 mt-0.5" />
                  <span>(236) 338-0659</span>
                </a>
              </li>
              <li>
                <a href="mailto:info@ssgroupservices.ca" className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
                  <span>info@ssgroupservices.ca</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-blue-500 mt-0.5" />
                <span>1605 34 ST, Vernon, BC<br />Canada V1T 5V4</span>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
                <span>Mon - Fri: 7:00 AM - 6:00 PM<br />Sat: 8:00 AM - 4:00 PM</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="border-t border-slate-800">
        <div className="container py-6">
          <p className="text-slate-400 text-sm font-bold uppercase tracking-wider mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>Service Areas</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {[['vernon','Vernon'],['kelowna','Kelowna'],['kamloops','Kamloops'],['armstrong','Armstrong'],['enderby','Enderby'],['sicamous','Sicamous'],['revelstoke','Revelstoke']].map(([slug, name]) => (
              <Link key={slug} to={`/painting-company/${slug}`} className="text-slate-500 hover:text-white text-sm transition-colors" data-testid={`footer-area-${slug}`}>
                Painting & Construction in {name}, BC
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} S&S Group Services. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm">
              www.ssgroupservices.ca
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
